export type DinoLanguage = "fr" | "fa";

export type DinoTutorExerciseType =
    | "mcq"
    | "binary"
    | "fill_blank"
    | "ordering";

export type JsonPrimitive = string | number | boolean | null;

export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];

export interface JsonObject {
    readonly [key: string]: JsonValue;
}

export interface DinoTutorContext {
    readonly language?: DinoLanguage;
    readonly route?: {
        readonly pathname: string;
        readonly search?: string;
    };
    readonly page?: {
        readonly id?: string;
        readonly kind?: string;
        readonly title?: string;
    };
    readonly content?: {
        readonly id: string;
        readonly type: string;
        readonly title?: string;
    };
    readonly lesson?: {
        readonly id: string;
        readonly title?: string;
        readonly level?: string;
    };
    readonly exercise?: {
        readonly id: string;
        readonly type?: DinoTutorExerciseType;
        readonly phase?: "idle" | "active" | "completed";
        readonly progress?: number;
        readonly objective?: string;
        readonly prompt?: string;
        readonly questionIndex?: number;
        readonly questionCount?: number;
        readonly outcome?: "correct" | "incorrect";
    };
    readonly userAction?: {
        readonly type: string;
        readonly target?: string;
        readonly occurredAt?: string;
    };
    readonly metadata?: JsonObject;
}

export type DinoTutorContextPatch = DinoTutorContext;

export type DinoContextListener = (
    snapshot: Readonly<DinoTutorContext>
) => void;

interface SourceContribution {
    readonly sequence: number;
    readonly value: DinoTutorContext;
}

export interface DinoContextTopic {
    readonly publish: (
        patch: DinoTutorContextPatch,
        source: string
    ) => () => void;
    readonly removeSource: (source: string) => void;
    readonly subscribe: (listener: DinoContextListener) => () => void;
    readonly getSnapshot: () => Readonly<DinoTutorContext>;
    readonly reset: () => void;
}

const SENSITIVE_METADATA_KEY =
    /(?:answer|authorization|cookie|credential|email|jwt|password|phone|response|secret|session|token)/iu;

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (
        value === null
        || typeof value !== "object"
    ) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value) as unknown;

    return prototype === Object.prototype || prototype === null;
}

function sanitizeJsonValue(
    value: unknown,
    seen: WeakSet<object>
): JsonValue | undefined {
    if (
        value === null
        || typeof value === "string"
        || typeof value === "boolean"
    ) {
        return value;
    }

    if (typeof value === "number") {
        return Number.isFinite(value)
            ? value
            : undefined;
    }

    if (Array.isArray(value)) {
        if (seen.has(value)) {
            return undefined;
        }

        seen.add(value);

        const result = value.flatMap(item => {
            const sanitized = sanitizeJsonValue(item, seen);

            return sanitized === undefined
                ? []
                : [sanitized];
        });

        seen.delete(value);
        return result;
    }

    if (!isPlainObject(value) || seen.has(value)) {
        return undefined;
    }

    seen.add(value);

    const result: Record<string, JsonValue> = {};

    for (const [key, child] of Object.entries(value)) {
        if (SENSITIVE_METADATA_KEY.test(key)) {
            continue;
        }

        const sanitized = sanitizeJsonValue(child, seen);

        if (sanitized !== undefined) {
            result[key] = sanitized;
        }
    }

    seen.delete(value);
    return result;
}

function deepFreeze<T>(value: T): Readonly<T> {
    if (
        value !== null
        && typeof value === "object"
        && !Object.isFrozen(value)
    ) {
        Object.freeze(value);

        for (const child of Object.values(value)) {
            deepFreeze(child);
        }
    }

    return value;
}

function sanitizePatch(patch: DinoTutorContextPatch): DinoTutorContext {
    const serialized = sanitizeJsonValue(
        patch,
        new WeakSet()
    );

    if (!isPlainObject(serialized)) {
        return {};
    }

    return deepFreeze(serialized) as DinoTutorContext;
}

function mergeObjects(
    base: Record<string, unknown>,
    contribution: Record<string, unknown>
): Record<string, unknown> {
    const merged: Record<string, unknown> = {
        ...base
    };

    for (const [key, value] of Object.entries(contribution)) {
        const previous = merged[key];

        merged[key] = isPlainObject(previous) && isPlainObject(value)
            ? mergeObjects(previous, value)
            : value;
    }

    return merged;
}

const EMPTY_CONTEXT = deepFreeze({}) as Readonly<DinoTutorContext>;

export function createDinoContextTopic(): DinoContextTopic {
    const sources = new Map<string, SourceContribution>();
    const listeners = new Set<DinoContextListener>();
    let sequence = 0;
    let snapshot = EMPTY_CONTEXT;

    function rebuildSnapshot(): void {
        const contributions = [...sources.values()]
            .sort((left, right) => left.sequence - right.sequence);

        const merged = contributions.reduce<Record<string, unknown>>(
            (result, contribution) => mergeObjects(
                result,
                contribution.value as Record<string, unknown>
            ),
            {}
        );

        snapshot = deepFreeze(merged) as Readonly<DinoTutorContext>;

        for (const listener of [...listeners]) {
            listener(snapshot);
        }
    }

    function removeSource(source: string): void {
        if (!sources.delete(source)) {
            return;
        }

        rebuildSnapshot();
    }

    return {
        publish(patch, source) {
            const normalizedSource = source.trim();

            if (normalizedSource.length === 0) {
                throw new Error("A Dino context source must have a name.");
            }

            const publicationSequence = ++sequence;

            sources.set(
                normalizedSource,
                {
                    sequence: publicationSequence,
                    value: sanitizePatch(patch)
                }
            );
            rebuildSnapshot();

            return () => {
                const current = sources.get(normalizedSource);

                // A stale React effect must not remove a newer publication.
                if (current?.sequence === publicationSequence) {
                    removeSource(normalizedSource);
                }
            };
        },
        removeSource,
        subscribe(listener) {
            listeners.add(listener);

            return () => {
                listeners.delete(listener);
            };
        },
        getSnapshot() {
            return snapshot;
        },
        reset() {
            sources.clear();
            sequence = 0;
            rebuildSnapshot();
        }
    };
}

const defaultTopic = createDinoContextTopic();

export { defaultTopic as dinoContextTopic };
